import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Loading from '../../components/common/Loading';
import Title from '../../components/common/Title';
import { apiRequest, apiFormRequest } from '../../utils';
import AccreditationForm from './AccreditationForm';
import LegalDocuments from '../company/LegalDocuments';
import './Supplier.css';

export const StatusPill = ({ request }) => {
  const { t } = useTranslation();
  if (!request) return null;
  const cls = request.status === 'confirmed' ? 'is-ok' : (request.late ? 'is-late' : 'is-open');
  return <span className={`ops-pill ${cls}`}>{t(`opsStatus_${request.status}`)}</span>;
};

const missingLine = (t, missing) => missing.map((m) => (
  m.reason === 'expired' ? t('opsExpiresBeforeEvent', { field: m.label }) : m.label
)).join(', ');

/* When (and where) one goodiebag / raffle item is delivered, or when a marketplace stand is
   installed. The day is picked from the days the organiser allows (Logistics in Machina) — a free
   date only while none are set. Whoever cannot make any of them says so in the note, which then
   stands in for the day. "Where" starts on the event's venue — most deliveries go there.
   When the organiser FIXED the day (and maybe the slot) in Machina's Request box, there is
   nothing to pick: both are shown as text and the company only confirms, or says in the note
   why it cannot. */
const dayLabel = (iso, lang) => new Date(`${iso}T00:00:00`).toLocaleDateString(lang || undefined,
  { weekday: 'long', day: 'numeric', month: 'long' });

const ContributionCard = ({ c, venue, days, onSave, busy }) => {
  const { t, i18n } = useTranslation();
  const isStand = c.kind === 'marketplace' || c.kind === 'company_booth';
  const [date, setDate] = useState(c.date || '');
  const [win, setWin] = useState(c.time_window || '');
  const [where, setWhere] = useState(c.location || venue || '');
  const [duration, setDuration] = useState(c.install_duration || '');
  const [note, setNote] = useState(c.note || '');
  const fixed = Boolean(c.fixed && c.date);
  const done = fixed ? !c.waiting : (c.status === 'confirmed' || c.status === 'received');
  let slot = '';
  if (fixed && c.slot_from) {
    slot = c.slot_until ? t('opsSlotBetween', { from: c.slot_from, to: c.slot_until })
      : t('opsSlotFrom', { from: c.slot_from });
  }
  // The days on offer: the allowed ones, plus the one already on the row (planned by us or
  // picked earlier) so it never silently disappears from the list.
  const offered = [...(days || [])];
  if (c.date && !offered.some((d) => d.date === c.date)) offered.push({ date: c.date, from: '', to: '' });
  offered.sort((a, b) => a.date.localeCompare(b.date));
  const picked = offered.find((d) => d.date === date);
  const hours = picked && (picked.from || picked.to) ? t('opsBetween', { from: picked.from || '…', to: picked.to || '…' }) : '';
  return (
    <div className="ops-contrib">
      <div className="ops-contrib__head">
        <span className="ops-contrib__kind">{t(`opsKind_${c.kind}`)}</span>
        <span>{c.description}{c.quantity ? ` × ${c.quantity}` : ''}</span>
        {done && <span className="ops-pill is-ok">{t('opsConfirmed')}</span>}
      </div>
      <div className="ops-contrib__form">
        <label className="ops-field">
          <span className="ops-field__label">{isStand ? t('opsInstallDay') : t('opsDeliverOn')}{fixed ? '' : ' *'}</span>
          {fixed ? (
            <strong className="ops-fixed">{dayLabel(c.date, i18n.language)}</strong>
          ) : offered.length > 0 ? (
            <select value={date} onChange={(e) => setDate(e.target.value)}>
              <option value="">{t('opsPickDay')}</option>
              {offered.map((d) => (
                <option key={d.date} value={d.date}>
                  {dayLabel(d.date, i18n.language)}{d.from || d.to ? ` (${d.from || '…'}–${d.to || '…'})` : ''}
                </option>
              ))}
            </select>
          ) : (
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          )}
        </label>
        {slot ? (
          <label className="ops-field">
            <span className="ops-field__label">{t('opsSlot')}</span>
            <strong className="ops-fixed">{slot}</strong>
          </label>
        ) : (
          <label className="ops-field">
            <span className="ops-field__label">{isStand ? t('opsArrivalTime') : t('opsTimeWindow')}</span>
            <input type="text" value={win} placeholder={hours || (isStand ? '09:30' : '10:00–12:00')}
                   onChange={(e) => setWin(e.target.value)} />
          </label>
        )}
        <label className="ops-field">
          <span className="ops-field__label">{t('opsInstallDuration')}</span>
          <input type="text" value={duration} placeholder={t('opsInstallDurationHint')}
                 onChange={(e) => setDuration(e.target.value)} />
        </label>
        {!isStand && (
          <label className="ops-field">
            <span className="ops-field__label">{t('opsWhere')}</span>
            <input type="text" value={where} onChange={(e) => setWhere(e.target.value)} />
          </label>
        )}
        <label className="ops-field">
          <span className="ops-field__label">{fixed ? t('opsFixedNote') : t('opsDeviationNote')}</span>
          <textarea rows={2} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        </label>
        <button type="button" className="ops-btn" disabled={busy || !(date || note.trim())}
                onClick={() => onSave(c.id, { date, time_window: win, location: where,
                                              install_duration: duration, note })}>
          {done ? t('opsUpdate') : t('opsConfirmThis')}
        </button>
      </div>
    </div>
  );
};

/* People or vehicles: the supplier's roster with a tick per row for THIS event. A row that
   misses something the venue needs says what; "+ Add" opens the form and ticks the new row. */
const RosterCard = ({ title, rows, selected, fields, labelOf, onToggle, onSave, onArchive, busy, addLabel }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(null);   // a row, or 'new'
  return (
    <Card>
      <h3 className="ops-card-title">{title}</h3>
      {!rows.length && editing !== 'new' && <p className="ops-hint">{t('opsRosterEmpty')}</p>}
      {rows.map((r) => (
        <div key={r.id} className="ops-roster-row">
          {editing && editing.id === r.id ? (
            <AccreditationForm fields={fields} initial={r} busy={busy}
                               onCancel={() => setEditing(null)}
                               onSubmit={(form) => onSave(form).then(() => setEditing(null))} />
          ) : (
            <>
              <label className="ops-roster-row__tick">
                <input type="checkbox" checked={selected.includes(r.id.toLowerCase())}
                       onChange={() => onToggle(r.id)} disabled={busy} />
                <span>
                  <span className="ops-roster-row__name">{labelOf(r)}</span>
                  {r.missing.length > 0 && <span className="ops-roster-row__missing">{missingLine(t, r.missing)}</span>}
                </span>
              </label>
              <span className="ops-roster-row__actions">
                <button type="button" className="ops-link" disabled={busy} onClick={() => setEditing(r)}>{t('edit')}</button>
                <button type="button" className="ops-link ops-link--muted" disabled={busy}
                        onClick={() => window.confirm(t('opsRemoveConfirm')) && onArchive(r.id)}>{t('opsRemove')}</button>
              </span>
            </>
          )}
        </div>
      ))}
      {editing === 'new' ? (
        <AccreditationForm fields={fields} initial={null} busy={busy}
                           onCancel={() => setEditing(null)}
                           onSubmit={(form) => onSave(form).then(() => setEditing(null))} />
      ) : (
        <button type="button" className="ops-btn ops-btn--ghost" disabled={busy} onClick={() => setEditing('new')}>+ {addLabel}</button>
      )}
    </Card>
  );
};

/* Also the staff's read-only look at a company's page (/supplier/preview/<token>, from the mail
   preview in Machina): same page, its data from /api/ops-preview, every button switched off. */
const SupplierEvent = () => {
  const { eventId, token } = useParams();
  const preview = Boolean(token);
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = useCallback(() => {
    if (preview) {
      apiRequest('GET', `/ops-preview/${token}`).then(setData)
        .catch((e) => setError(e.message === 'opsPreviewExpired' ? 'opsPreviewExpired' : 'opsNotFound'));
      return;
    }
    apiRequest('GET', `/supplier/event/${eventId}`).then(setData)
      .catch((e) => setError(e.message === 'notAuthorized' ? 'opsNoAccess' : 'opsNotFound'));
  }, [eventId, token, preview]);
  useEffect(load, [load]);

  const run = async (promise, okKey) => {
    if (preview) return;
    setBusy(true);
    setFlash(null);
    try {
      const next = await promise;
      if (next && next.event) setData(next);
      if (okKey) setFlash({ ok: true, key: okKey });
    } catch (e) {
      if (e.data && e.data.event) setData(e.data);
      setFlash({ ok: false, key: (e.data && e.data.error) || 'opsSaveFailed' });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Base activeNav="home"><Card><p className="ops-hint">{t(error)}</p></Card></Base>;
  if (!data) return <Base activeNav="home"><Loading /></Base>;

  const req = data.request;
  const ev = data.event;
  const locked = busy || preview;
  const toggle = (kind, id) => {
    const key = kind === 'people' ? 'selected_people' : 'selected_vehicles';
    const cur = data[key];
    const lid = id.toLowerCase();
    const next = cur.includes(lid) ? cur.filter((x) => x !== lid) : [...cur, lid];
    run(apiRequest('POST', `/supplier/event/${eventId}/selection`,
                   kind === 'people' ? { person_ids: next } : { vehicle_ids: next }));
  };
  const personLabel = (p) => `${p.first_name} ${p.last_name}`.trim() || t('opsUnnamed');
  const vehicleLabel = (v) => [v.values.plate, v.values.make, v.values.vehicle_type].filter(Boolean).join(' · ') || t('opsVehicle');

  return (
    <Base activeNav="home">
      <Title>{ev.name}</Title>
      <p className="ops-sub">{[ev.date, ev.venue].filter(Boolean).join(' · ')} · {data.supplier.name}</p>
      {preview && <div className="ops-flash is-ok">{t('opsPreviewBanner', { name: data.supplier.name })}</div>}

      {req && (
        <Card>
          <div className="ops-status">
            <StatusPill request={req} />
            {req.deadline && <span>{t('opsDeadline', { date: req.deadline })}</span>}
          </div>
          {req.status === 'confirmed' && <p className="ops-ok">{t('opsAllConfirmed')}</p>}
          {req.status !== 'confirmed' && data.problems.length > 0 && (
            <ul className="ops-problems">{data.problems.map((p) => <li key={p}>{p}</li>)}</ul>
          )}
        </Card>
      )}
      {flash && <div className={`ops-flash ${flash.ok ? 'is-ok' : 'is-bad'}`}>{t(flash.key)}</div>}

      {data.lines && data.lines.length > 0 && (
        <Card>
          <h3 className="ops-card-title">{t('opsYourAssignment')}</h3>
          {data.lines.map((l, i) => (
            <div key={i} className="ops-assign">
              <strong>{l.qty != null && l.qty !== '' ? `${l.qty}${l.unit ? ` ${l.unit}` : ''} × ` : ''}{l.item}</strong>
              <span>{[l.due && t('opsDeliverOnDate', { date: l.due }),
                      l.until && t('opsUntilDate', { date: l.until })]
                .filter(Boolean).join(' · ')}</span>
            </div>
          ))}
        </Card>
      )}

      {(data.ask_docs || (req && req.ask_trade_licence)) && !preview && (
        <Card>
          {/* A supplier we pay owes all three papers; a request may ask for just the trade licence. */}
          <LegalDocuments apiBase="/supplier/legal-docs"
                          hintKey={data.ask_docs ? 'opsLegalDocsHint' : 'opsTradeLicenceHint'}
                          only={data.ask_docs ? null : ['trade_licence']} onChange={load} />
        </Card>
      )}
      {req && req.ask_trade_licence && preview && (
        <Card>
          <h3 className="ops-card-title">{t('docTradeLicence')}</h3>
          <p className="ops-hint">{t('opsTradeLicenceHint')}</p>
        </Card>
      )}

      {!(data.lines && data.lines.length > 0) && data.assignment.length > 0 && (
        <Card>
          <h3 className="ops-card-title">{t('opsYourAssignment')}</h3>
          {data.assignment.map((a, i) => (
            <div key={i} className="ops-assign">
              <strong>{a.what}</strong>
              <span>{[a.install_date && t('opsInstall', { date: a.install_date }),
                      a.cleanup_date && t('opsCleanup', { date: a.cleanup_date }),
                      a.crew_count ? t('opsCrewCount', { n: a.crew_count }) : null]
                .filter(Boolean).join(' · ')}</span>
            </div>
          ))}
        </Card>
      )}

      {data.contributions.length > 0 && (
        <Card>
          <h3 className="ops-card-title">{t('opsYourContribution')}</h3>
          {data.contributions.map((c) => (
            <ContributionCard key={c.id} c={c} venue={ev.venue} busy={locked}
                              days={(data.days || {})[c.kind]}
                              onSave={(id, body) => run(apiRequest('POST', `/supplier/event/${eventId}/contribution/${id}`, body), 'opsSaved')} />
          ))}
        </Card>
      )}

      {req && req.ask_persons && (
        <RosterCard title={t('opsWhoIsComing')} rows={data.people} selected={data.selected_people}
                    fields={data.person_fields} labelOf={personLabel} busy={locked} addLabel={t('opsAddPerson')}
                    onToggle={(id) => toggle('people', id)}
                    onSave={(form) => run(apiFormRequest(`/supplier/people?event=${eventId}`, form), 'opsSaved')}
                    onArchive={(id) => run(apiRequest('POST', `/supplier/people/${id}/archive?event=${eventId}`))} />
      )}
      {req && req.ask_vehicles && (
        <RosterCard title={t('opsVehicles')} rows={data.vehicles} selected={data.selected_vehicles}
                    fields={data.vehicle_fields} labelOf={vehicleLabel} busy={locked} addLabel={t('opsAddVehicle')}
                    onToggle={(id) => toggle('vehicles', id)}
                    onSave={(form) => run(apiFormRequest(`/supplier/vehicles?event=${eventId}`, form), 'opsSaved')}
                    onArchive={(id) => run(apiRequest('POST', `/supplier/vehicles/${id}/archive?event=${eventId}`))} />
      )}

      {req && (
        <div className="ops-confirm-bar">
          <button type="button" className="ops-btn ops-btn--big"
                  disabled={locked || data.problems.length > 0}
                  onClick={() => run(apiRequest('POST', `/supplier/event/${eventId}/confirm`), 'opsThanksConfirmed')}>
            {req.status === 'confirmed' ? t('opsConfirmAgain') : t('opsConfirm')}
          </button>
        </div>
      )}

      {data.our_contact && (
        <Card>
          <h3 className="ops-card-title">{t('opsQuestions')}</h3>
          <p className="ops-contact">
            {data.our_contact.name}
            {data.our_contact.phone && <> · <a href={`tel:${data.our_contact.phone}`}>{data.our_contact.phone}</a></>}
            {data.our_contact.email && <> · <a href={`mailto:${data.our_contact.email}`}>{data.our_contact.email}</a></>}
          </p>
        </Card>
      )}
      {!preview && <p className="ops-sub"><Link to="/supplier">{t('opsAllMyEvents')}</Link></p>}
    </Base>
  );
};

export default SupplierEvent;
